import React, { useState } from 'react';
import { CheckIcon, XIcon, ShieldCheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { CTAButton } from '../components/CTAButton';
import { TestimonialCard } from '../components/TestimonialCard';
import { FAQAccordion } from '../components/FAQAccordion';
import { SocialProofPopup } from '../components/SocialProofPopup';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/uoidhXJ9bReL6wWqPyBVSu/Mockup.png';
const BONUS_1_IMAGE = 'https://cdn.magicpatterns.com/uploads/jFNxg95HMggTTRsYAaGmCg/Mockup_-_Bono_1.png';
const BONUS_2_IMAGE = 'https://cdn.magicpatterns.com/uploads/kxjUqme1jRwNLHDN1Y98HA/Mockup_-_Bono_2.png';
const BONUS_3_IMAGE = 'https://cdn.magicpatterns.com/uploads/6cPmFsb6bpUsRFV3mF3xcB/Mockup_-_Bono_3.png';
const PREVIEW_PAGES: string[] = [];
const CHECKOUT_LINK = 'https://checkout.tumundoglobal.online/cart/45190695944383:1';
const SECTION_LINK = '#compra';
export function SalesPage() {
  const [currentPage, setCurrentPage] = useState(0);
  const nextPage = () => {
    setCurrentPage(prev => (prev + 1) % PREVIEW_PAGES.length);
  };
  const prevPage = () => {
    setCurrentPage(prev => (prev - 1 + PREVIEW_PAGES.length) % PREVIEW_PAGES.length);
  };
  return <div className="min-h-screen bg-white font-sans text-gray-900 pb-20" data-id="element-34">
      <CountdownTimer data-id="element-35" />
      <SocialProofPopup data-id="element-36" />

      {/* HERO SECTION */}
      <section className="pt-8 pb-12 px-4 md:pt-12 md:pb-20 bg-gradient-to-b from-white to-gray-50" data-id="element-37">
        <div className="max-w-4xl mx-auto text-center" data-id="element-38">
          <div className="inline-flex items-center justify-center bg-green-100 text-green-700 px-6 py-2 rounded-full mb-6" data-id="element-39">
            <span className="text-sm md:text-base font-medium uppercase tracking-wide" data-id="element-40">
              🥣 PARA MAMÁS QUE QUIEREN ALIMENTAR CON SEGURIDAD SIN VIVIR CON
              MIEDO
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-gray-900 mb-8 leading-tight" data-id="element-41">
            Kit Recetas para Bebé - Listo para Usar
          </h1>

          <div className="mb-6" data-id="element-42">
            <a href={SECTION_LINK} data-id="element-43">
              <img src={PRODUCT_IMAGE} alt="Kit Recetas para Bebé" className="w-full max-w-lg mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300 cursor-pointer" data-id="element-44" />
            </a>
          </div>

          <p className="text-xl md:text-2xl font-bold text-gray-900 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-45">
            Alimentá a tu bebé desde los 6 meses hasta los 3 años{' '}
            <span className="bg-yellow-300 px-1" data-id="element-46">
              con seguridad y tranquilidad
            </span>
            , sin volver a preguntarte qué cocinar cada día.
          </p>

          <CTAButton data-id="element-47">👉 SÍ, QUIERO EL KIT AHORA</CTAButton>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-48">
        <div className="max-w-3xl mx-auto" data-id="element-49">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-50">
            Si querés alimentar bien a tu bebé… pero por dentro estás llena de
            dudas, dejame preguntarte algo honesto:
          </h2>

          <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-51">
            <p className="font-medium" data-id="element-52">
              ¿Te pasa que cada día te preguntás qué cocinarle?
            </p>
            <p className="font-medium" data-id="element-53">
              ¿Buscás recetas durante horas y terminás más confundida?
            </p>
            <p className="font-medium" data-id="element-54">¿Te da miedo que se atragante?</p>
            <p className="font-medium" data-id="element-55">
              ¿Te preocupa no introducir bien los alimentos?
            </p>
            <p className="font-medium" data-id="element-56">
              ¿Sentís que otras mamás saben más que vos?
            </p>
            <p className="font-medium" data-id="element-57">
              ¿Te angustia equivocarte justo en algo tan importante?
            </p>

            <p className="text-xl font-bold text-gray-900 mt-8" data-id="element-58">
              No estás exagerando. No estás siendo dramática. Y no te falta
              información.
            </p>
            <p data-id="element-59">Pero algo sí está pasando.</p>
            <p data-id="element-60">Y no es que no seas capaz.</p>
            <p className="text-center font-bold text-2xl mt-8 border-l-4 border-[#dc2626] pl-4 py-2 bg-red-50" data-id="element-61">
              Es que nadie te enseñó cómo hacerlo paso a paso.
            </p>
          </div>

          <div className="mt-12" data-id="element-62">
            <CTAButton data-id="element-63">👉 QUIERO TERMINAR CON ESTA CONFUSIÓN</CTAButton>
          </div>
        </div>
      </section>

      {/* PAIN POINTS SECTION */}
      <section className="py-16 px-4 bg-gray-900 text-white" data-id="element-64">
        <div className="max-w-4xl mx-auto" data-id="element-65">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-yellow-400" data-id="element-66">
            El problema que nadie te explica
          </h2>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-67">
            <div className="space-y-4" data-id="element-68">
              <p className="text-xl font-medium mb-6" data-id="element-69">
                Si esto te pasa, probablemente te resulte familiar:
              </p>
              <ul className="space-y-4" data-id="element-70">
                <li className="flex items-start gap-3" data-id="element-71">
                  <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-72" />
                  <span className="text-lg" data-id="element-73">
                    Vivís improvisando cada comida
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-74">
                  <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-75" />
                  <span className="text-lg" data-id="element-76">
                    Guardás capturas de recetas que nunca organizás
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-77">
                  <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-78" />
                  <span className="text-lg" data-id="element-79">
                    Te da miedo introducir ciertos alimentos
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-80">
                  <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-81" />
                  <span className="text-lg" data-id="element-82">
                    No sabés cómo cortarlos correctamente
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-83">
                  <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-84" />
                  <span className="text-lg" data-id="element-85">
                    No tenés claro qué corresponde según la edad
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-86">
                  <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-87" />
                  <span className="text-lg" data-id="element-88">
                    Sentís presión por "hacerlo bien"
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700" data-id="element-89">
              <p className="text-lg mb-6" data-id="element-90">
                Y lo peor es esa sensación interna de:
              </p>
              <p className="text-2xl font-bold italic text-gray-300 mb-8" data-id="element-91">
                "Quiero hacerlo bien… pero no sé si lo estoy haciendo
                correctamente."
              </p>

              <div className="space-y-2 font-bold text-red-400 text-xl" data-id="element-92">
                <p data-id="element-93">❌ No es que seas inexperta.</p>
                <p data-id="element-94">❌ No es que necesites más fuerza de voluntad.</p>
                <p data-id="element-95">❌ No es que tu bebé sea difícil.</p>
              </div>

              <p className="mt-6 text-gray-300" data-id="element-96">
                La alimentación complementaria no es intuitiva cuando nadie te
                da estructura.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-97">
            <p className="text-xl mb-8" data-id="element-98">
              👉 No se trata de buscar más recetas.
            </p>
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 max-w-3xl mx-auto mb-8" data-id="element-99">
              <h3 className="text-xl font-bold text-red-400 mb-4" data-id="element-100">
                Tu problema no es cocinar.
              </h3>
              <p className="text-gray-300 mb-4" data-id="element-101">
                Es no tener un plan organizado por etapas.
              </p>
              <p className="text-gray-300" data-id="element-102">Y nadie te lo entregó así.</p>
            </div>
            <p className="text-2xl font-bold text-[#22c55e]" data-id="element-103">
              👉 Kit Recetas para Bebé – Listo para Usar nace para eliminar esa
              improvisación.
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-104">
        <div className="max-w-5xl mx-auto" data-id="element-105">
          <div className="text-center mb-16" data-id="element-106">
            <span className="bg-green-100 text-green-800 px-4 py-1 rounded-full font-bold text-sm uppercase tracking-wide" data-id="element-107">
              ✅ LA SOLUCIÓN
            </span>
            <h2 className="text-4xl md:text-5xl font-black mt-6 mb-4" data-id="element-108">
              Kit Recetas para Bebé – Listo para Usar
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-109">
              Un sistema práctico, organizado y progresivo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-110">
            <div className="bg-green-50 p-8 rounded-2xl border border-green-100" data-id="element-111">
              <h3 className="text-2xl font-bold mb-6" data-id="element-112">Diseñado para que:</h3>
              <ul className="space-y-4" data-id="element-113">
                <li className="flex items-center gap-3" data-id="element-114">
                  <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-115" />
                  <span className="text-lg font-medium text-gray-800" data-id="element-116">
                    Sepas exactamente qué ofrecer según la edad
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-117">
                  <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-118" />
                  <span className="text-lg font-medium text-gray-800" data-id="element-119">
                    Tengas cortes seguros sin miedo
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-120">
                  <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-121" />
                  <span className="text-lg font-medium text-gray-800" data-id="element-122">
                    Introduzcas alérgenos con tranquilidad
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-123">
                  <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-124" />
                  <span className="text-lg font-medium text-gray-800" data-id="element-125">
                    Evites improvisar cada día
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-126">
                  <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-127" />
                  <span className="text-lg font-medium text-gray-800" data-id="element-128">
                    Acompañes el desarrollo real de tu bebé
                  </span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col justify-center items-center text-center p-8 bg-gray-50 rounded-2xl border border-gray-100" data-id="element-129">
              <h3 className="text-2xl font-bold mb-6 text-gray-900" data-id="element-130">
                Así de simple:
              </h3>
              <div className="space-y-6 text-xl font-bold text-gray-800" data-id="element-131">
                <div className="flex items-start gap-4" data-id="element-132">
                  <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-1" data-id="element-133">
                    1
                  </span>
                  <span className="text-left" data-id="element-134">Abrís el tomo según la edad</span>
                </div>
                <div className="flex items-start gap-4" data-id="element-135">
                  <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-1" data-id="element-136">
                    2
                  </span>
                  <span className="text-left" data-id="element-137">Elegís la receta</span>
                </div>
                <div className="flex items-start gap-4" data-id="element-138">
                  <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-1" data-id="element-139">
                    3
                  </span>
                  <span className="text-left" data-id="element-140">Cocinás con seguridad</span>
                </div>
              </div>
              <p className="mt-8 text-lg text-gray-600 italic" data-id="element-141">
                Sin dudas. Sin caos. Sin buscar en 10 blogs distintos.
              </p>
            </div>
          </div>

          <CTAButton data-id="element-142">👉 QUIERO EMPEZAR HOY</CTAButton>
        </div>
      </section>

      {/* MODULES SECTION */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-143">
        <div className="max-w-5xl mx-auto" data-id="element-144">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-145">
            📘 ¿QUÉ INCLUYE EXACTAMENTE EL KIT?
          </h2>
          <p className="text-center text-xl text-gray-600 mb-12" data-id="element-146">
            📦 6 TOMOS ORGANIZADOS POR EDAD
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-147">
            <a href={SECTION_LINK} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" data-id="element-148">
              <div className="text-4xl mb-4" data-id="element-149">📗</div>
              <h3 className="text-xl font-bold mb-2" data-id="element-150">
                Tomo 1 — 6–8 meses | Primeros Sabores
              </h3>
              <p className="text-gray-600" data-id="element-151">
                Texturas blandas. Inicio BLW seguro. Base de confianza.
              </p>
            </a>

            <a href={SECTION_LINK} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" data-id="element-152">
              <div className="text-4xl mb-4" data-id="element-153">📘</div>
              <h3 className="text-xl font-bold mb-2" data-id="element-154">
                Tomo 2 — 8–10 meses | Más Variedad
              </h3>
              <p className="text-gray-600" data-id="element-155">
                Pinza digital. Nuevas combinaciones simples.
              </p>
            </a>

            <a href={SECTION_LINK} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" data-id="element-156">
              <div className="text-4xl mb-4" data-id="element-157">📙</div>
              <h3 className="text-xl font-bold mb-2" data-id="element-158">
                Tomo 3 — 10–12 meses | Más Autonomía
              </h3>
              <p className="text-gray-600" data-id="element-159">
                Transición a texturas reales y comidas familiares adaptadas.
              </p>
            </a>

            <a href={SECTION_LINK} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" data-id="element-160">
              <div className="text-4xl mb-4" data-id="element-161">📕</div>
              <h3 className="text-xl font-bold mb-2" data-id="element-162">
                Tomo 4 — 12–18 meses | Comer en Familia
              </h3>
              <p className="text-gray-600" data-id="element-163">
                Platos sin sal ni ultraprocesados adaptados a todos.
              </p>
            </a>

            <a href={SECTION_LINK} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" data-id="element-164">
              <div className="text-4xl mb-4" data-id="element-165">📒</div>
              <h3 className="text-xl font-bold mb-2" data-id="element-166">
                Tomo 5 — 18–36 meses | Etapa Selectiva
              </h3>
              <p className="text-gray-600" data-id="element-167">
                Recetas estratégicas anti-rechazo y anticrisis.
              </p>
            </a>

            <a href={SECTION_LINK} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" data-id="element-168">
              <div className="text-4xl mb-4" data-id="element-169">📓</div>
              <h3 className="text-xl font-bold mb-2" data-id="element-170">
                Tomo 6 — Organización Inteligente
              </h3>
              <p className="text-gray-600" data-id="element-171">
                Batch cooking + menús semanales listos para usar.
              </p>
            </a>
          </div>

          <div className="mt-12 bg-white p-6 rounded-xl border border-gray-200 max-w-3xl mx-auto" data-id="element-172">
            <ul className="space-y-3 text-lg" data-id="element-173">
              <li className="flex items-center gap-3" data-id="element-174">
                <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-175" />
                <span className="font-medium" data-id="element-176">Más de 350 recetas reales</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-177">
                <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-178" />
                <span className="font-medium" data-id="element-179">Organizadas por edad</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-180">
                <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-181" />
                <span className="font-medium" data-id="element-182">Sin sal añadida</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-183">
                <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-184" />
                <span className="font-medium" data-id="element-185">Sin azúcar refinada</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-186">
                <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-187" />
                <span className="font-medium" data-id="element-188">
                  Con cortes seguros explicados
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-189">
                <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-190" />
                <span className="font-medium" data-id="element-191">
                  Pensadas para mamás ocupadas
                </span>
              </li>
            </ul>
          </div>

          <p className="text-center text-lg font-medium mt-12 text-gray-700" data-id="element-192">
            👉 Todo estructurado para que sepas qué hacer y cuándo hacerlo.
          </p>
        </div>
      </section>

      {/* BEFORE VS AFTER */}
      <section className="py-16 px-4 bg-white" data-id="element-193">
        <div className="max-w-4xl mx-auto" data-id="element-194">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-195">
            Antes vs Después
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-196">
            <div className="bg-red-50 p-8 rounded-2xl border border-red-100" data-id="element-197">
              <h3 className="text-xl font-bold text-red-800 mb-6 text-center" data-id="element-198">
                Antes del Kit
              </h3>
              <ul className="space-y-4" data-id="element-199">
                <li className="flex items-center gap-3" data-id="element-200">
                  <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-201" />
                  <span className="text-gray-700" data-id="element-202">Improvisación diaria</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-203">
                  <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-204" />
                  <span className="text-gray-700" data-id="element-205">
                    Miedo a atragantamientos
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-206">
                  <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-207" />
                  <span className="text-gray-700" data-id="element-208">Dudas con alergias</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-209">
                  <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-210" />
                  <span className="text-gray-700" data-id="element-211">Estrés al planificar</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-212">
                  <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-213" />
                  <span className="text-gray-700" data-id="element-214">Peleas en la mesa</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-215">
                  <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-216" />
                  <span className="text-gray-700" data-id="element-217">
                    Sensación de no saber si lo haces bien
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-green-50 p-8 rounded-2xl border border-green-100 shadow-lg transform md:-translate-y-4" data-id="element-218">
              <h3 className="text-xl font-bold text-green-800 mb-6 text-center" data-id="element-219">
                Después del Kit
              </h3>
              <ul className="space-y-4" data-id="element-220">
                <li className="flex items-center gap-3" data-id="element-221">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-222" />
                  <span className="font-bold text-gray-800" data-id="element-223">
                    Seguridad real
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-224">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-225" />
                  <span className="font-bold text-gray-800" data-id="element-226">
                    Cortes claros y seguros
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-227">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-228" />
                  <span className="font-bold text-gray-800" data-id="element-229">
                    Introducción ordenada de alimentos
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-230">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-231" />
                  <span className="font-bold text-gray-800" data-id="element-232">Menús listos</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-233">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-234" />
                  <span className="font-bold text-gray-800" data-id="element-235">
                    Bebé más autónomo
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-236">
                  <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-237" />
                  <span className="font-bold text-gray-800" data-id="element-238">
                    Confianza como mamá
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 px-4 bg-[#0f172a] text-white" data-id="element-239">
        <div className="max-w-5xl mx-auto" data-id="element-240">
          <div className="text-center mb-12" data-id="element-241">
            <span className="bg-yellow-400 text-black px-4 py-1 rounded-full font-bold text-sm uppercase tracking-wide animate-pulse" data-id="element-242">
              🎁 BONOS ESPECIALES INCLUIDOS HOY
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-6 mb-4" data-id="element-243">
              ¡GRATIS POR HOY!
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-244">
            <a href={SECTION_LINK} className="bg-[#1e293b] rounded-xl p-6 border border-[#334155] flex flex-col items-center text-center hover:border-yellow-400 transition-colors cursor-pointer" data-id="element-245">
              <div className="w-full aspect-square max-w-[270px] mb-6 flex items-center justify-center rounded-lg bg-[#1e293b]" data-id="element-246">
                <img src={BONUS_1_IMAGE} alt="Bono 1 - Noches Tranquilas" className="w-full h-full object-contain drop-shadow-xl" data-id="element-247" />
              </div>
              <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-248">
                GRATIS HOY
              </span>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-249">
                🎁 Bono #1 — Noches Tranquilas
              </h3>
              <p className="text-gray-300 text-sm" data-id="element-250">
                Guía práctica para mejorar el descanso del bebé. Porque
                alimentación y sueño van de la mano.
              </p>
            </a>

            <a href={SECTION_LINK} className="bg-[#1e293b] rounded-xl p-6 border border-[#334155] flex flex-col items-center text-center hover:border-yellow-400 transition-colors cursor-pointer" data-id="element-251">
              <div className="w-full aspect-square max-w-[270px] mb-6 flex items-center justify-center rounded-lg bg-[#1e293b]" data-id="element-252">
                <img src={BONUS_2_IMAGE} alt="Bono 2 - Alérgenos Seguros" className="w-full h-full object-contain drop-shadow-xl" data-id="element-253" />
              </div>
              <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-254">
                GRATIS HOY
              </span>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-255">
                🎁 Bono #2 — Alérgenos Seguros
              </h3>
              <p className="text-gray-300 text-sm" data-id="element-256">
                Qué ofrecer. Cuándo ofrecer. Qué observar. Qué hacer ante una
                reacción.
              </p>
            </a>

            <a href={SECTION_LINK} className="bg-[#1e293b] rounded-xl p-6 border border-[#334155] flex flex-col items-center text-center hover:border-yellow-400 transition-colors cursor-pointer" data-id="element-257">
              <div className="w-full aspect-square max-w-[270px] mb-6 flex items-center justify-center rounded-lg bg-[#1e293b]" data-id="element-258">
                <img src={BONUS_3_IMAGE} alt="Bono 3 - Guía de Cortes Seguros BLW" className="w-full h-full object-contain drop-shadow-xl" data-id="element-259" />
              </div>
              <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-260">
                GRATIS HOY
              </span>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-261">
                🎁 Bono #3 — Guía de Cortes Seguros BLW
              </h3>
              <p className="text-gray-300 text-sm" data-id="element-262">
                Cómo cortar cada alimento según edad. Evita sustos innecesarios
                y gana seguridad real.
              </p>
            </a>
          </div>

          <div className="mt-12 text-center space-y-2 text-lg font-medium" data-id="element-263">
            <p data-id="element-264">👉 Los tomos te dicen qué cocinar.</p>
            <p data-id="element-265">👉 Los bonos te ayudan a hacerlo con confianza.</p>
            <p className="text-yellow-400 font-bold text-xl mt-4" data-id="element-266">
              Eso es lo que lo convierte en un sistema completo.
            </p>
          </div>

          <div className="mt-12" data-id="element-267">
            <CTAButton data-id="element-268">👉 QUIERO MIS BONOS AHORA</CTAButton>
          </div>
        </div>
      </section>

      {/* OFFER STACK SECTION */}
      <section id="compra" className="py-16 px-4 bg-gradient-to-b from-gray-900 to-gray-800" data-id="element-269">
        <div className="max-w-4xl mx-auto" data-id="element-270">
          <div className="text-center mb-10" data-id="element-271">
            <span className="bg-red-500 text-white px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide animate-pulse inline-block mb-4" data-id="element-272">
              ⏰ OFERTA ESPECIAL
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase leading-tight" data-id="element-273">
              POR TIEMPO LIMITADO
            </h2>
          </div>

          <div className="bg-white rounded-3xl p-6 md:p-12 shadow-2xl" data-id="element-274">
            <h3 className="text-2xl md:text-3xl font-black text-center mb-8 text-gray-900" data-id="element-275">
              📦 Lo que recibes hoy:
            </h3>

            <div className="space-y-4 mb-10" data-id="element-276">
              <div className="flex items-center gap-3 text-lg md:text-xl border-b border-gray-200 pb-3" data-id="element-277">
                <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-278" />
                <span className="font-bold text-gray-800" data-id="element-279">
                  6 tomos completos
                </span>
              </div>
              <div className="flex items-center gap-3 text-lg md:text-xl border-b border-gray-200 pb-3" data-id="element-280">
                <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-281" />
                <span className="font-bold text-gray-800" data-id="element-282">
                  +350 recetas organizadas
                </span>
              </div>
              <div className="flex items-center gap-3 text-lg md:text-xl border-b border-gray-200 pb-3" data-id="element-283">
                <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-284" />
                <span className="font-bold text-gray-800" data-id="element-285">
                  Bono Noches Tranquilas
                </span>
              </div>
              <div className="flex items-center gap-3 text-lg md:text-xl border-b border-gray-200 pb-3" data-id="element-286">
                <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-287" />
                <span className="font-bold text-gray-800" data-id="element-288">
                  Bono Alérgenos Seguros
                </span>
              </div>
              <div className="flex items-center gap-3 text-lg md:text-xl border-b border-gray-200 pb-3" data-id="element-289">
                <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-290" />
                <span className="font-bold text-gray-800" data-id="element-291">
                  Bono Guía de Cortes BLW
                </span>
              </div>
              <div className="flex items-center gap-3 text-lg md:text-xl" data-id="element-292">
                <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-293" />
                <span className="font-bold text-gray-800" data-id="element-294">
                  Acceso de por vida
                </span>
              </div>
            </div>

            <div className="text-center mb-8" data-id="element-295">
              <p className="text-gray-500 text-xl mb-2" data-id="element-296">
                Precio regular:{' '}
                <span className="line-through decoration-red-500 decoration-2" data-id="element-297">
                  49.990 ARS
                </span>
              </p>
              <p className="text-5xl md:text-7xl font-black text-[#22c55e]" data-id="element-298">
                🔥 19.990 ARS
              </p>
              <p className="text-gray-600 mt-2 font-medium" data-id="element-299">
                Un solo pago. Sin suscripciones.
              </p>
            </div>

            <div className="flex flex-col items-center justify-center w-full" data-id="element-300">
              <a href={CHECKOUT_LINK} className="relative group flex items-center justify-center gap-2 w-full md:max-w-2xl px-6 py-5 md:py-6 bg-[#22c55e] hover:bg-[#16a34a] text-white text-center rounded-xl shadow-xl hover:shadow-2xl transform transition-all duration-200 hover:-translate-y-1 border-b-4 border-[#15803d] active:border-b-0 active:translate-y-1 text-xl md:text-2xl font-bold uppercase tracking-normal" data-id="element-301">
                👉 OBTENER MI KIT HOY
              </a>
            </div>
          </div>

          <div className="mt-10 flex flex-col md:flex-row items-center gap-6 bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20" data-id="element-302">
            <ShieldCheckIcon className="w-16 h-16 text-yellow-400 flex-shrink-0" data-id="element-303" />
            <div className="text-center md:text-left" data-id="element-304">
              <h3 className="text-xl font-bold mb-2 text-white" data-id="element-305">
                Garantía de 7 días sin riesgo
              </h3>
              <p className="text-gray-300" data-id="element-306">
                Pruébalo durante 7 días. Si no sientes más seguridad y claridad
                al planificar las comidas, te devolvemos el 100% del dinero. Sin
                preguntas. Sin complicaciones.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PREVIEW PAGES SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-307">
        <div className="max-w-4xl mx-auto" data-id="element-308">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-309">
            📖 Revisa algunas hojas
          </h2>
          <p className="text-center text-gray-600 mb-10" data-id="element-310">
            Mira una muestra real de cómo está organizado el contenido.
          </p>

          <div className="bg-gray-100 rounded-2xl p-12 text-center" data-id="element-311">
            <p className="text-gray-500 text-lg" data-id="element-312">
              (Sección visual con capturas de recetas, cortes y menú semanal -
              Próximamente)
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-313">
        <div className="max-w-6xl mx-auto" data-id="element-314">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-315">
            Lo que dicen otras mamás
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-316">
            <TestimonialCard name="Carolina" age={32} location="Buenos Aires" quote="Antes vivía con miedo constante de que se atragantara. Con el kit aprendí los cortes seguros y ahora cocino con tranquilidad. Mi bebé come de todo." data-id="element-317" />
            <TestimonialCard name="Sofía" age={28} location="Córdoba" quote="Pasé de buscar recetas en Instagram cada día a tener todo organizado por edad. Me ahorro horas de estrés y mi hija está súper bien alimentada." data-id="element-318" />
            <TestimonialCard name="Valentina" age={35} location="Rosario" quote="Lo mejor es que no tengo que improvisar más. Abro el tomo, elijo la receta y listo. Siento que finalmente sé lo que estoy haciendo." data-id="element-319" />
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-6" data-id="element-320">
            <TestimonialCard name="Lucía" age={30} location="Mendoza" quote="Los bonos de alérgenos y cortes seguros me salvaron. Introduje huevo, pescado y frutos secos sin miedo gracias a las instrucciones claras." data-id="element-321" />
            <TestimonialCard name="Martina" age={27} location="La Plata" quote="Mi bebé está en la etapa selectiva y las recetas del tomo 5 funcionan increíble. Come verduras que antes rechazaba. Estoy feliz." data-id="element-322" />
            <TestimonialCard name="Camila" age={33} location="Santa Fe" quote="Trabajo full time y el batch cooking del tomo 6 cambió mi vida. Cocino una vez y tengo comida lista para toda la semana. Súper práctico." data-id="element-323" />
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-324">
        <div className="max-w-3xl mx-auto" data-id="element-325">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-326">
            Preguntas Frecuentes
          </h2>
          <FAQAccordion data-id="element-327" />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 bg-gray-900 text-white text-center" data-id="element-328">
        <div className="max-w-3xl mx-auto" data-id="element-329">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-330">
            No se trata solo de comida
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed" data-id="element-331">
            Se trata de tranquilidad. Se trata de confianza. Se trata de
            seguridad.
          </p>

          <p className="text-xl text-gray-300 mb-10 leading-relaxed" data-id="element-332">
            Se trata de acompañar a tu bebé en una de las etapas más importantes
            de su desarrollo…
          </p>

          <p className="text-2xl text-white font-bold mb-10" data-id="element-333">Y hacerlo bien.</p>

          <a href={SECTION_LINK} className="inline-flex flex-col items-center justify-center px-12 py-5 md:py-6 bg-[#22c55e] hover:bg-[#16a34a] text-white text-center rounded-xl shadow-xl hover:shadow-2xl transform transition-all duration-200 hover:-translate-y-1 border-b-4 border-[#15803d] active:border-b-0 active:translate-y-1" data-id="element-334">
            <span className="text-lg md:text-xl font-bold uppercase tracking-normal" data-id="element-335">
              👉 QUIERO MI KIT AHORA
            </span>
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 bg-black text-gray-500 text-center text-sm" data-id="element-336">
        <p data-id="element-337">© 2024 Kit Recetas para Bebé. Todos los derechos reservados.</p>
        <p className="mt-2" data-id="element-338">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
        </p>
      </footer>
    </div>;
}